/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import AnalyticsService from 'cio/services/analytics';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { isNone, isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import FormValidations from 'cio/utils/form-validations';
import { pruneObject } from 'cio/utils/objects';
import CONTACT_OPTIONS from 'cio/utils/integrations/contact-options';
import isValidEmail from 'cio/utils/validators/email';

export type NewIntegration = {
  name: string;
  outcome: string | null;
  comments: string | null;
  wouldPay?: boolean | null;
  price?: string | null;
  userEmail?: string | null;
  contacts?: string | null;
  category?: string | null;
};

const Validations = {
  userEmail(value: string) {
    if (isNone(value) || isEmpty(value.trim()) || !isValidEmail(value.trim())) {
      return "This email address doesn't look quite right";
    }

    return true;
  },
  name(value: string) {
    if (isNone(value) || isEmpty(value.trim())) {
      return 'An integration name is required';
    }

    return true;
  },
};

interface IntegrationsNewIntegrationFormArgs {
  category?: string;
  onSubmit?: (newIntegration: NewIntegration) => unknown;
  onClose?: () => unknown;
  public?: boolean;
}

export default class IntegrationsNewIntegrationForm extends Component<IntegrationsNewIntegrationFormArgs> {
  @service analytics!: AnalyticsService;

  get onSubmit() {
    return this.args.onSubmit ?? (() => {});
  }
  get onClose() {
    return this.args.onClose ?? (() => {});
  }
  get public() {
    return this.args.public ?? false;
  }

  @tracked formValues: NewIntegration = {
    name: '',
    outcome: null,
    wouldPay: null,
    price: null,
    comments: null,
    userEmail: null,
    contacts: null,
  };
  contactOptions = CONTACT_OPTIONS;
  @tracked validations!: FormValidations;

  constructor(owner: unknown, args: IntegrationsNewIntegrationFormArgs) {
    super(owner, args);

    this.validations = FormValidations.create({ validations: Validations, context: this, modelKey: 'formValues' });
  }

  get from() {
    return this.public ? 'Public' : 'In-App';
  }

  get submitDisabled() {
    if (this.public) {
      return !!this.validations.hasErrors || !this.validations.isTouched;
    }

    return !!(this.validations.errors as any)?.name || !this.validations.isTouched;
  }

  @action
  async setWouldPay(value: boolean) {
    let newValues: Partial<NewIntegration> = {
      wouldPay: value,
    };

    if (!value) {
      newValues.price = null;
    }
    this.formValues = {
      ...this.formValues,
      ...newValues,
    };

    // Because the public version of this component hides a required field, this will always throw
    // on the public version. We check it before submission.
    try {
      await this.validations.validateModel();
    } catch {}
  }

  @action
  async onFormSubmit(evt: Event) {
    evt.preventDefault();

    try {
      await this.validations.validateModel();
    } catch {}

    if (this.submitDisabled) {
      return;
    }

    let integrationData = { ...this.formValues };

    if (this.args.category != null) {
      integrationData.category = this.args.category;
    }

    let prunedData = pruneObject(integrationData) as any;

    this.analytics.track('integration', 'request_completed', {
      from: this.from,
      requestType: 'New',
      ...prunedData,
    });

    this.onSubmit(prunedData);
  }

  @action
  setContact(opt: { value: string }) {
    this.formValues = {
      ...this.formValues,
      contacts: opt.value,
    };
  }
}
